import { BulkOperations } from '@openx/components-header/BulkOperations/BulkOperations';
import type { ActionButtonProps } from '@openx/components-header/HeaderActions/ActionButton';
import {
  type ListLalSegment,
  type ListSegment,
  lalSegmentRowActionType,
  segmentRowActionType,
} from '@openx/types/segments';
import { type JSX, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useLalSegmentsOperations } from '../hooks/useLalSegmentsOperations';
import { useSegmentsOperations } from '../hooks/useSegmentsOperations';
import { validateSeedsAllowedStatus } from '../LalSegmentsForm/validationSchema';
import { useSegmentContext } from '../utils/context/SegmentContext';
import { useShowLal } from '../utils/useShowLal';
import { ArchiveSegmentDialogContent } from './ArchiveSegmentsDialog/ArchiveSegmentDialogContent';

type SegmentsBulkOperationsProps = {
  selectedSegments: ListSegment[] | ListLalSegment[];
  closeAndUnselect: () => void;
};

export const SegmentsBulkOperations = ({
  selectedSegments,
  closeAndUnselect,
}: SegmentsBulkOperationsProps): JSX.Element => {
  const { t } = useTranslation();
  const { useLalSegmentsWithDetailsFetch, useSegmentsLinkedAudiencesFetch } = useSegmentContext();

  const navigate = useNavigate();

  const { fetchSegmentsLinkedAudiences } = useSegmentsLinkedAudiencesFetch();
  const { fetchLalSegmentsWithDetails } = useLalSegmentsWithDetailsFetch();
  const { callSegmentAction, setConfirmationDisabled, loadingAction } = useSegmentsOperations();
  const {
    callLalSegmentAction,
    setConfirmationDisabled: setLalConfirmationDisabled,
    loadingAction: lalLoadingAction,
  } = useLalSegmentsOperations();

  const segmentIds = useMemo(() => selectedSegments.map(segment => segment.id), [selectedSegments]);

  const isLalSegments = (segments: ListSegment[] | ListLalSegment[]): segments is ListLalSegment[] => {
    return segments.every((segment): segment is ListLalSegment => 'lookalike_name' in segment);
  };

  const showLal = useShowLal();
  const isLalSegment = isLalSegments(selectedSegments);

  const isValidSeedsStatuses = validateSeedsAllowedStatus(selectedSegments);

  const isCreateLookalikeSegmentAllowed = useMemo(
    () =>
      !isLalSegment &&
      new Set(selectedSegments.map(segment => segment.provider?.name)).size === 1 &&
      new Set(selectedSegments.map(segment => segment.account?.name)).size === 1 &&
      isValidSeedsStatuses,
    [selectedSegments, isLalSegment, isValidSeedsStatuses]
  );

  const notAllowedMessage = useMemo(() => {
    if (new Set(selectedSegments.map(segment => segment.provider?.name)).size > 1) {
      return t('Lookalike Segments support only one Provider.');
    } else if (!isValidSeedsStatuses) {
      return t('Segments must be in Ready status to create a LAL Segment');
    }

    return t('Lookalike Segments support only one Account.');
  }, [selectedSegments, t, isValidSeedsStatuses]);

  const actions: ActionButtonProps[] = [];

  if (isLalSegments(selectedSegments)) {
    actions.push({
      color: 'error',
      disabled: !!lalLoadingAction,
      label: t('Archive'),
      loading: lalLoadingAction === lalSegmentRowActionType.DELETE,
      onClick: () => {
        callLalSegmentAction({
          action: lalSegmentRowActionType.DELETE,
          content: (
            <ArchiveSegmentDialogContent
              data={selectedSegments}
              isLalSegments={true}
              setConfirmationDisabled={setLalConfirmationDisabled}
            />
          ),
          isBulkMode: true,
          onExtraAction: () => fetchLalSegmentsWithDetails(segmentIds),
          onFinish: () => closeAndUnselect(),
          segments: selectedSegments,
        });
      },
    });
  } else {
    actions.push(
      {
        color: 'error',
        disabled: !!loadingAction,
        label: t('Archive'),
        loading: loadingAction === segmentRowActionType.DELETE,
        onClick: () => {
          callSegmentAction({
            action: segmentRowActionType.DELETE,
            content: (
              <ArchiveSegmentDialogContent data={selectedSegments} setConfirmationDisabled={setConfirmationDisabled} />
            ),
            isBulkMode: true,
            onExtraAction: () => fetchSegmentsLinkedAudiences(segmentIds),
            onFinish: () => closeAndUnselect(),
            segments: selectedSegments,
          });
        },
      },
      {
        color: 'default',
        isHidden: !showLal,
        isAllowed: isCreateLookalikeSegmentAllowed,
        notAllowedMessage: notAllowedMessage,
        label: t('Create Lookalike Segment'),
        loading: false,
        onClick: () => {
          navigate('/segments/lookalike/create', {
            state: {
              segments: selectedSegments,
            },
          });
        },
      }
    );
  }

  return (
    <BulkOperations<ListSegment | ListLalSegment>
      selectedItems={selectedSegments}
      actions={actions}
      onClose={() => closeAndUnselect()}
    />
  );
};
